import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { DateRange } from "react-day-picker";
import { useAuth } from "~/context/AuthContext";
import { useSubscription } from "~/context/SubscriptionContext";
import { apiClient } from "~/lib/api-client";
import {
  computePreviousPeriodRange,
  getLatestDataDate,
  getPresetRange,
  getPreviousTemplateRange,
  parseAnalysisDate,
  type PresetId,
} from "~/lib/date-range-presets";
import { computeSevenDayTrialWindow, isActiveTrialSubscription } from "~/lib/trial";
import type { AIVisibilityRunsResponse } from "~/types/AIVisibilityTypes";
import type { PromptCountry } from "~/types/PromptTypes";

const LOCAL_STORAGE_FILTERS_KEY = "zerply_analytics_filters";
const CUSTOM_RANGE_EXPIRY_MS = 6 * 60 * 60 * 1000;
const FILTER_LOOKUP_TIMEOUT_MS = 4000;
const DEFAULT_RANGE: RangeValue = "last_30d";

export type RangeValue = PresetId;

export type ComparisonMode = "previous_period" | "custom";

export type SourcesMetricTab = "domains" | "urls" | "decay";

export interface ApplyDateSelectionParams {
  primaryRange: DateRange;
  primaryPreset: RangeValue;
  compareEnabled: boolean;
  comparisonMode: ComparisonMode;
  comparisonRange?: DateRange;
}

export function computePreviousPeriod(
  fromDate: Date,
  toDate: Date
): { from: Date; to: Date } {
  return computePreviousPeriodRange(fromDate, toDate);
}

export interface AnalyticsFilters {
  selectedRange: RangeValue;
  selectedBrands: string[];
  llmId: string | null;
  selectedTagIds: number[];
  customRange?: {
    from: string;
    to: string;
  };
  sourcesLlmId?: string | null;
  countryCode?: string | null;
  selectedDomainCategories?: string[];
  selectedUrlCategories?: string[];
  metricTab?: SourcesMetricTab;
  savedAt?: number;
  sourcesBrands?: string[];
  compareEnabled?: boolean;
  comparisonMode?: ComparisonMode;
  comparisonRange?: {
    from: string;
    to: string;
  };
}

interface AIVisibilityFiltersContextType {
  selectedRange: RangeValue;
  setSelectedRange: (range: RangeValue) => void;
  selectedBrands: string[];
  setSelectedBrands: (brands: string[], persist?: boolean) => void;
  llmId: string | null;
  setLlmId: (id: string | null) => void;
  countryCode: string | null;
  setCountryCode: (code: string | null) => void;
  answerCountries: PromptCountry[];
  sourcesLlmId: string | null;
  setSourcesLlmId: (id: string | null) => void;
  selectedTagIds: number[];
  setSelectedTagIds: (tagIds: number[]) => void;
  customRange: DateRange | undefined;
  setCustomRange: (range: DateRange | undefined) => void;
  fromDate: Date;
  toDate: Date;
  datesReady: boolean;
  latestDataDate: Date;
  showCustomPicker: boolean;
  setShowCustomPicker: (show: boolean) => void;
  tempCustomRange: DateRange | undefined;
  setTempCustomRange: (range: DateRange | undefined) => void;
  tempSelectedRange: RangeValue;
  setTempSelectedRange: (range: RangeValue) => void;
  originalCustomRange: DateRange | undefined;
  setOriginalCustomRange: (range: DateRange | undefined) => void;
  applyDateRange: () => void;
  saveFiltersToStorage: (updates: Partial<AnalyticsFilters>) => void;
  selectedCategories: Set<string>;
  setSelectedCategories: (categories: Set<string>) => void;
  metricTab: SourcesMetricTab;
  setMetricTab: (tab: SourcesMetricTab) => void;
  isBrandsInitialized: boolean;
  setIsBrandsInitialized: (val: boolean) => void;
  sourcesBrands: string[];
  setSourcesBrands: (brands: string[]) => void;
  compareEnabled: boolean;
  setCompareEnabled: (enabled: boolean) => void;
  comparisonMode: ComparisonMode;
  setComparisonMode: (mode: ComparisonMode) => void;
  comparisonRange: DateRange | undefined;
  comparisonFromDate: Date | null;
  comparisonToDate: Date | null;
  applyAnalyticsDateSelection: (params: ApplyDateSelectionParams) => void;
}

const AIVisibilityFiltersContext = createContext<
  AIVisibilityFiltersContextType | undefined
>(undefined);

export function useAIVisibilityFilters() {
  const context = useContext(AIVisibilityFiltersContext);
  if (!context) {
    throw new Error(
      "useAIVisibilityFilters must be used within AIVisibilityFiltersProvider"
    );
  }
  return context;
}

function calculateDates(
  range: RangeValue,
  customDates?: { from: string; to: string },
  allTimeStart?: Date,
  latestAnalysisDate?: Date | null
) {
  if (range === "custom" && customDates?.from && customDates?.to) {
    const fromDate = new Date(customDates.from);
    const toDate = new Date(customDates.to);

    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(23, 59, 59, 999);

    return {
      from: fromDate,
      to: toDate,
    };
  }

  return getPresetRange(range, new Date(), allTimeStart, latestAnalysisDate);
}

export function AIVisibilityFiltersProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const initialDatesSetRef = useRef(false);
  const [latestAnalysisDate, setLatestAnalysisDate] = useState<
    Date | null | undefined
  >(undefined);
  const [answerCountries, setAnswerCountries] = useState<
    PromptCountry[] | undefined
  >(undefined);
  const datesReady =
    latestAnalysisDate !== undefined && answerCountries !== undefined;

  const getStoredFilters = useCallback((): AnalyticsFilters => {
    try {
      let currentTeamId: number | undefined = user?.team_id;
      if (!currentTeamId) {
        const storedTeamId = localStorage.getItem("p_active_team_id");
        if (storedTeamId) {
          const parsed = parseInt(storedTeamId, 10);
          if (!isNaN(parsed)) {
            currentTeamId = parsed;
          }
        }
      }

      if (!currentTeamId) return {} as AnalyticsFilters;

      const teamKey = `${LOCAL_STORAGE_FILTERS_KEY}_${currentTeamId}`;
      const stored = localStorage.getItem(teamKey);

      if (stored) return JSON.parse(stored);

      const legacyStored = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
      if (legacyStored) {
        const parsed = JSON.parse(legacyStored);
        if (!parsed.teamId || parsed.teamId === currentTeamId) {
          const { teamId: _, ...filters } = parsed;
          localStorage.setItem(teamKey, JSON.stringify(filters));
          localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
          return filters;
        }
      }

      return {} as AnalyticsFilters;
    } catch {
      return {} as AnalyticsFilters;
    }
  }, [user?.team_id]);

  const saveFiltersToStorage = useCallback((updates: Partial<AnalyticsFilters>) => {
    try {
      let currentTeamId: number | undefined = user?.team_id;
      if (!currentTeamId) {
        const storedTeamId = localStorage.getItem("p_active_team_id");
        if (storedTeamId) {
          const parsed = parseInt(storedTeamId, 10);
          if (!isNaN(parsed)) {
            currentTeamId = parsed;
          }
        }
      }

      if (!currentTeamId) return;

      const existing = getStoredFilters();
      const merged = { ...existing, ...updates };
      localStorage.setItem(
        `${LOCAL_STORAGE_FILTERS_KEY}_${currentTeamId}`,
        JSON.stringify(merged)
      );
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  }, [user?.team_id, getStoredFilters]);

  const storedFilters = getStoredFilters();

  const allTimeStart = useMemo(() => {
    const activeTeam = user?.teams?.find(
      (team) => String(team.id) === String(user?.team_id)
    );
    return activeTeam?.created_at ? new Date(activeTeam.created_at) : undefined;
  }, [user]);

  const latestDataDate = useMemo(
    () => getLatestDataDate(new Date(), latestAnalysisDate),
    [latestAnalysisDate]
  );

  const isCustomExpired =
    storedFilters.selectedRange === "custom" &&
    storedFilters.savedAt &&
    Date.now() - storedFilters.savedAt > CUSTOM_RANGE_EXPIRY_MS;

  const initialRange: RangeValue = isCustomExpired
    ? DEFAULT_RANGE
    : storedFilters.selectedRange || DEFAULT_RANGE;
  const initialCustomDates = isCustomExpired
    ? undefined
    : storedFilters.customRange;
  const initialDates = calculateDates(
    initialRange,
    initialCustomDates,
    allTimeStart,
    latestAnalysisDate
  );

  const [selectedRange, setSelectedRangeState] =
    useState<RangeValue>(initialRange);
  const [fromDate, setFromDate] = useState<Date>(initialDates.from);
  const [toDate, setToDate] = useState<Date>(initialDates.to);
  const [customRange, setCustomRangeState] = useState<DateRange | undefined>(
    () => {
      if (
        initialRange === "custom" &&
        initialCustomDates?.from &&
        initialCustomDates?.to
      ) {
        return {
          from: new Date(initialCustomDates.from),
          to: new Date(initialCustomDates.to),
        };
      }
      return undefined;
    }
  );
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [tempCustomRange, setTempCustomRange] = useState<DateRange | undefined>(
    undefined
  );
  const [tempSelectedRange, setTempSelectedRange] =
    useState<RangeValue>(initialRange);
  const [originalCustomRange, setOriginalCustomRange] = useState<
    DateRange | undefined
  >(undefined);
  const [selectedBrands, setSelectedBrandsState] = useState<string[]>(
    storedFilters.selectedBrands || []
  );
  const [isBrandsInitialized, setIsBrandsInitializedState] = useState<boolean>(
    storedFilters.selectedBrands !== undefined
  );
  const [llmId, setLlmIdState] = useState<string | null>(
    storedFilters.llmId || null
  );
  const [sourcesLlmId, setSourcesLlmIdState] = useState<string | null>(
    storedFilters.sourcesLlmId || null
  );
  const [storedCountryCode, setStoredCountryCode] = useState<string | null>(
    storedFilters.countryCode || null
  );
  const countryCode =
    answerCountries &&
    answerCountries.length > 1 &&
    answerCountries.some((country) => country.code === storedCountryCode)
      ? storedCountryCode
      : null;
  const [selectedTagIds, setSelectedTagIdsState] = useState<number[]>(
    storedFilters.selectedTagIds || []
  );
  const [selectedDomainCategories, setSelectedDomainCategoriesState] = useState<Set<string>>(
    new Set(storedFilters.selectedDomainCategories || [])
  );
  const [selectedUrlCategories, setSelectedUrlCategoriesState] = useState<Set<string>>(
    new Set(storedFilters.selectedUrlCategories || [])
  );
  const [metricTab, setMetricTabState] = useState<SourcesMetricTab>(
    storedFilters.metricTab || "domains"
  );
  const [sourcesBrands, setSourcesBrandsState] = useState<string[]>(
    storedFilters.sourcesBrands || []
  );
  const [compareEnabled, setCompareEnabledState] = useState<boolean>(
    Boolean(storedFilters.compareEnabled)
  );
  const [comparisonMode, setComparisonModeState] = useState<ComparisonMode>(
    storedFilters.comparisonMode || "previous_period"
  );
  const [comparisonCustomRange, setComparisonCustomRangeState] = useState<
    DateRange | undefined
  >(() => {
    if (
      storedFilters.comparisonRange?.from &&
      storedFilters.comparisonRange?.to
    ) {
      return {
        from: new Date(storedFilters.comparisonRange.from),
        to: new Date(storedFilters.comparisonRange.to),
      };
    }
    return undefined;
  });

  const setSelectedRange = (range: RangeValue) => {
    setSelectedRangeState(range);
    setTempSelectedRange(range);
    saveFiltersToStorage({ selectedRange: range });
    if (range === "custom" && customRange?.from && customRange?.to) {
      const dates = calculateDates(
        range,
        {
          from: customRange.from.toISOString(),
          to: customRange.to.toISOString(),
        },
        allTimeStart,
        latestAnalysisDate
      );
      setFromDate(dates.from);
      setToDate(dates.to);
    } else {
      const dates = calculateDates(
        range,
        undefined,
        allTimeStart,
        latestAnalysisDate
      );
      setFromDate(dates.from);
      setToDate(dates.to);
    }
  };

  const setSelectedBrands = (brands: string[], persist = true) => {
    setSelectedBrandsState(brands);
    setIsBrandsInitializedState(true);
    if (persist) {
      saveFiltersToStorage({ selectedBrands: brands });
    }
  };

  const setLlmId = (id: string | null) => {
    setLlmIdState(id);
    saveFiltersToStorage({ llmId: id });
  };

  const setCountryCode = (code: string | null) => {
    setStoredCountryCode(code);
    saveFiltersToStorage({ countryCode: code });
  };

  const setSourcesLlmId = (id: string | null) => {
    setSourcesLlmIdState(id);
    saveFiltersToStorage({ sourcesLlmId: id });
  };

  const setSelectedTagIds = (tagIds: number[]) => {
    setSelectedTagIdsState(tagIds);
    saveFiltersToStorage({ selectedTagIds: tagIds });
  };

  const setSelectedCategories = (categories: Set<string>) => {
    if (metricTab === "domains") {
      setSelectedDomainCategoriesState(categories);
      saveFiltersToStorage({ selectedDomainCategories: Array.from(categories) });
    } else {
      setSelectedUrlCategoriesState(categories);
      saveFiltersToStorage({ selectedUrlCategories: Array.from(categories) });
    }
  };

  const setMetricTab = (tab: SourcesMetricTab) => {
    setMetricTabState(tab);
    saveFiltersToStorage({ metricTab: tab });
  };

  const selectedCategories = metricTab === "domains" ? selectedDomainCategories : selectedUrlCategories;

  const setSourcesBrands = (brands: string[]) => {
    setSourcesBrandsState(brands);
    saveFiltersToStorage({ sourcesBrands: brands });
  };

  const setCompareEnabled = (enabled: boolean) => {
    setCompareEnabledState(enabled);
    saveFiltersToStorage({ compareEnabled: enabled });
  };

  const setComparisonMode = (mode: ComparisonMode) => {
    setComparisonModeState(mode);
    saveFiltersToStorage({ comparisonMode: mode });
  };

  const applyAnalyticsDateSelection = ({
    primaryRange,
    primaryPreset,
    compareEnabled: nextCompareEnabled,
    comparisonMode: nextComparisonMode,
    comparisonRange: nextComparisonRange,
  }: ApplyDateSelectionParams) => {
    const isCustomPrimary = primaryPreset === "custom";

    if (primaryRange.from && primaryRange.to) {
      setSelectedRangeState(primaryPreset);
      setTempSelectedRange(primaryPreset);
      setCustomRangeState(isCustomPrimary ? primaryRange : undefined);
      setFromDate(primaryRange.from);
      setToDate(primaryRange.to);
    }

    setCompareEnabledState(nextCompareEnabled);
    setComparisonModeState(nextComparisonMode);

    const persistComparisonCustom =
      nextComparisonMode === "custom" &&
      nextComparisonRange?.from &&
      nextComparisonRange?.to;

    if (persistComparisonCustom && nextComparisonRange) {
      setComparisonCustomRangeState(nextComparisonRange);
    }

    saveFiltersToStorage({
      selectedRange: primaryPreset,
      customRange:
        isCustomPrimary && primaryRange.from && primaryRange.to
          ? {
              from: primaryRange.from.toISOString(),
              to: primaryRange.to.toISOString(),
            }
          : undefined,
      savedAt: isCustomPrimary ? Date.now() : undefined,
      compareEnabled: nextCompareEnabled,
      comparisonMode: nextComparisonMode,
      comparisonRange:
        persistComparisonCustom && nextComparisonRange
          ? {
              from: nextComparisonRange.from!.toISOString(),
              to: nextComparisonRange.to!.toISOString(),
            }
          : undefined,
    });
  };

  const comparisonDates = useMemo<{ from: Date | null; to: Date | null }>(() => {
    if (!compareEnabled) {
      return { from: null, to: null };
    }
    if (
      comparisonMode === "custom" &&
      comparisonCustomRange?.from &&
      comparisonCustomRange?.to
    ) {
      return {
        from: comparisonCustomRange.from,
        to: comparisonCustomRange.to,
      };
    }
    if (selectedRange === "custom") {
      return computePreviousPeriodRange(fromDate, toDate);
    }
    return getPreviousTemplateRange(
      selectedRange,
      new Date(),
      allTimeStart,
      latestAnalysisDate
    );
  }, [
    compareEnabled,
    comparisonMode,
    comparisonCustomRange,
    selectedRange,
    fromDate,
    toDate,
    allTimeStart,
    latestAnalysisDate,
  ]);

  const setCustomRange = (range: DateRange | undefined) => {
    setCustomRangeState(range);
    if (range?.from && range?.to) {
      saveFiltersToStorage({
        selectedRange: "custom",
        customRange: {
          from: range.from.toISOString(),
          to: range.to.toISOString(),
        },
        savedAt: Date.now(),
      });
      const dates = calculateDates("custom", {
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      });
      setFromDate(dates.from);
      setToDate(dates.to);
    }
  };

  const applyDateRange = () => {
    if (tempCustomRange?.from && tempCustomRange?.to) {
      if (tempSelectedRange === "custom") {
        setCustomRangeState(tempCustomRange);
      }
      setSelectedRangeState(tempSelectedRange);
      saveFiltersToStorage({
        selectedRange: tempSelectedRange,
        customRange:
          tempSelectedRange === "custom"
            ? {
                from: tempCustomRange.from.toISOString(),
                to: tempCustomRange.to.toISOString(),
              }
            : undefined,
        savedAt: tempSelectedRange === "custom" ? Date.now() : undefined,
      });
      setFromDate(tempCustomRange.from);
      setToDate(tempCustomRange.to);
    }
  };

  const refreshRelativeDates = useCallback(() => {
    if (selectedRange === "custom") {
      const stored = getStoredFilters();
      if (
        stored.savedAt &&
        Date.now() - stored.savedAt > CUSTOM_RANGE_EXPIRY_MS
      ) {
        setSelectedRangeState(DEFAULT_RANGE);
        setCustomRangeState(undefined);
        const dates = calculateDates(
          DEFAULT_RANGE,
          undefined,
          allTimeStart,
          latestAnalysisDate
        );
        setFromDate(dates.from);
        setToDate(dates.to);
        saveFiltersToStorage({
          selectedRange: DEFAULT_RANGE,
          customRange: undefined,
          savedAt: undefined,
        });
      }
      return;
    }

    const dates = calculateDates(
      selectedRange,
      undefined,
      allTimeStart,
      latestAnalysisDate
    );

    setFromDate((prev) =>
      prev.getTime() !== dates.from.getTime() ? dates.from : prev
    );
    setToDate((prev) =>
      prev.getTime() !== dates.to.getTime() ? dates.to : prev
    );
  }, [
    selectedRange,
    allTimeStart,
    latestAnalysisDate,
    getStoredFilters,
    saveFiltersToStorage,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshRelativeDates();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshRelativeDates]);

  useEffect(() => {
    let cancelled = false;

    const giveUp = setTimeout(() => {
      if (!cancelled) setLatestAnalysisDate(null);
    }, FILTER_LOOKUP_TIMEOUT_MS);

    const loadLatestDataDate = async () => {
      try {
        const response = await apiClient.get<AIVisibilityRunsResponse>(
          "/ai-visibility-runs?page=1&per_page=5"
        );
        if (cancelled) return;
        clearTimeout(giveUp);

        const utcToday = new Date().toISOString().slice(0, 10);
        const todayRun = response.data?.data?.items?.find(
          (run) => run.analysis_date === utcToday
        );

        if (todayRun && todayRun.success_rate === 100) {
          setLatestAnalysisDate(parseAnalysisDate(todayRun.analysis_date));
          return;
        }

        setLatestAnalysisDate(null);
      } catch {
        if (!cancelled) {
          clearTimeout(giveUp);
          setLatestAnalysisDate(null);
        }
      }
    };

    loadLatestDataDate();
    return () => {
      cancelled = true;
      clearTimeout(giveUp);
    };
  }, [user?.team_id]);

  useEffect(() => {
    let cancelled = false;
    const giveUp = setTimeout(() => {
      if (!cancelled) setAnswerCountries([]);
    }, FILTER_LOOKUP_TIMEOUT_MS);

    apiClient
      .get<{ data: PromptCountry[] }>("/ai-visibility/countries")
      .then((response) => {
        if (!cancelled) setAnswerCountries(response.data?.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setAnswerCountries([]);
      })
      .finally(() => clearTimeout(giveUp));

    return () => {
      cancelled = true;
      clearTimeout(giveUp);
    };
  }, [user?.team_id]);

  useEffect(() => {
    refreshRelativeDates();
  }, [latestAnalysisDate, refreshRelativeDates]);

  useEffect(() => {
    if (showCustomPicker) {
      let rangeToUse: DateRange | undefined;

      if (selectedRange === "custom" && customRange?.from && customRange?.to) {
        rangeToUse = customRange;
      } else if (selectedRange !== "custom") {
        const dates = calculateDates(
          selectedRange,
          undefined,
          allTimeStart,
          latestAnalysisDate
        );
        rangeToUse = { from: dates.from, to: dates.to };
      } else {
        rangeToUse = { from: fromDate, to: toDate };
      }

      if (rangeToUse) {
        setTempCustomRange(rangeToUse);
        setTempSelectedRange(selectedRange);
        setOriginalCustomRange(rangeToUse);
      }
    }
  }, [
    showCustomPicker,
    customRange,
    selectedRange,
    fromDate,
    toDate,
    allTimeStart,
    latestAnalysisDate,
  ]);

  useEffect(() => {
    if (subscriptionLoading) return;
    if (initialDatesSetRef.current) return;

    const activeTeam = user?.teams?.find(
      (team) => team.id === user?.team_id
    );

    const window = computeSevenDayTrialWindow(activeTeam?.created_at);

    const shouldPinTrialWindow =
      isActiveTrialSubscription(subscription) ||
      Boolean(subscription?.trial_expired);

    if (shouldPinTrialWindow && window.trialStartDate && window.trialEndDate) {
      setFromDate(window.trialStartDate);
      setToDate(window.trialEndDate);
      setCustomRangeState({
        from: window.trialStartDate,
        to: window.trialEndDate,
      });
      setSelectedRangeState("custom");
      saveFiltersToStorage({
        selectedRange: "custom",
        customRange: {
          from: window.trialStartDate.toISOString(),
          to: window.trialEndDate.toISOString(),
        },
      });

      initialDatesSetRef.current = true;
    }
  }, [user, subscription, subscriptionLoading, saveFiltersToStorage]);

  const value: AIVisibilityFiltersContextType = {
    selectedRange,
    setSelectedRange,
    selectedBrands,
    setSelectedBrands,
    llmId,
    setLlmId,
    countryCode,
    setCountryCode,
    answerCountries: answerCountries ?? [],
    sourcesLlmId,
    setSourcesLlmId,
    selectedTagIds,
    setSelectedTagIds,
    customRange,
    setCustomRange,
    fromDate,
    toDate,
    datesReady,
    latestDataDate,
    showCustomPicker,
    setShowCustomPicker,
    tempCustomRange,
    setTempCustomRange,
    tempSelectedRange,
    setTempSelectedRange,
    originalCustomRange,
    setOriginalCustomRange,
    applyDateRange,
    saveFiltersToStorage,
    selectedCategories,
    setSelectedCategories,
    metricTab,
    setMetricTab,
    isBrandsInitialized,
    setIsBrandsInitialized: setIsBrandsInitializedState,
    sourcesBrands,
    setSourcesBrands,
    compareEnabled,
    setCompareEnabled,
    comparisonMode,
    setComparisonMode,
    comparisonRange: comparisonCustomRange,
    comparisonFromDate: comparisonDates.from,
    comparisonToDate: comparisonDates.to,
    applyAnalyticsDateSelection,
  };

  return (
    <AIVisibilityFiltersContext.Provider value={value}>
      {children}
    </AIVisibilityFiltersContext.Provider>
  );
}
